import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Trash2, X, Loader2, ArrowRight } from 'lucide-react';
import clsx from 'clsx';
import {
  useDeleteCategory,
  useAdminCategories,
  getCategoryProductCount,
} from '../../services/adminService';

const overlay = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modal = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', damping: 25, stiffness: 300 } },
  exit: { opacity: 0, scale: 0.95, y: 10, transition: { duration: 0.15 } },
};

/**
 * Confirmation modal for deleting a category.
 * Shows product count and offers reassign-to dropdown.
 *
 * @param {{ category: object|null, onClose: () => void }} props
 */
export function DeleteCategoryModal({ category, onClose }) {
  const deleteMutation = useDeleteCategory();
  const { data: allCategories } = useAdminCategories();

  const [productCount, setProductCount] = useState(0);
  const [checking, setChecking] = useState(true);
  const [reassignTo, setReassignTo] = useState('');

  // Check product count on mount
  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    setChecking(true);
    getCategoryProductCount(category.id).then((count) => {
      if (!cancelled) {
        setProductCount(count);
        setChecking(false);
      }
    });
    return () => { cancelled = true; };
  }, [category]);

  const otherCategories = (allCategories || []).filter((c) => c.id !== category?.id);

  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Escape') onClose(); },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleDelete = () => {
    deleteMutation.mutate(
      { id: category.id, reassignTo: reassignTo || null },
      { onSuccess: onClose }
    );
  };

  if (!category) return null;

  return (
    <AnimatePresence>
      {category && (
        <>
          <motion.div
            key="del-cat-backdrop"
            variants={overlay}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm"
          />

          <motion.div
            key="del-cat-modal"
            variants={modal}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <div className="bg-brand-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <Trash2 size={20} className="text-error" />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-brand-dark">Delete Category</h3>
                    <p className="text-xs text-brand-gray">Remove "{category.name}"</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4">
                {checking ? (
                  <div className="flex items-center gap-2 text-sm text-brand-gray">
                    <Loader2 size={14} className="animate-spin" />
                    Checking products in this category...
                  </div>
                ) : productCount > 0 ? (
                  <>
                    <div className="flex items-start gap-3 p-3 bg-warning/10 border border-warning/20 rounded-xl">
                      <AlertTriangle size={18} className="text-warning flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-bold text-brand-dark">
                          {productCount} product{productCount !== 1 ? 's' : ''} in this category
                        </p>
                        <p className="text-brand-gray mt-0.5">
                          Choose where to move them, or they'll become uncategorized.
                        </p>
                      </div>
                    </div>

                    {/* Reassign dropdown */}
                    <div>
                      <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                        Move products to
                      </label>
                      <div className="relative">
                        <ArrowRight size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
                        <select
                          value={reassignTo}
                          onChange={(e) => setReassignTo(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 appearance-none"
                        >
                          <option value="">Leave uncategorized</option>
                          {otherCategories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.icon ? `${c.icon} ` : ''}{c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-brand-gray">
                    No products are using this category. It's safe to delete.
                  </p>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-ui-border flex items-center gap-3 bg-ui-background">
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending || checking}
                  className={clsx(
                    'flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl transition-all',
                    !deleteMutation.isPending && !checking
                      ? 'bg-error hover:bg-red-700 text-brand-white shadow-lg'
                      : 'bg-gray-200 text-brand-gray cursor-not-allowed'
                  )}
                >
                  {deleteMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete Category'}
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                {deleteMutation.isError && (
                  <p className="text-xs text-error ml-auto">
                    {deleteMutation.error?.message || 'Failed to delete'}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
